import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, X, Loader2, Send, Search } from "lucide-react";
import { adminApi } from "@/lib/api";

const SEGMENTS = [["all", "Tutti i clienti"], ["active", "Con campagna attiva"], ["completed", "Con campagna conclusa"], ["none", "Senza nessuna campagna"], ["manual", "Selezione manuale"]];
const SEG_LABEL = Object.fromEntries(SEGMENTS);
const field = "w-full bg-[#f4f6f7] border border-[#d5dbde] text-[#2B3036] text-sm px-3 py-2 outline-none focus:border-[#E0262C]";

const ManualPicker = ({ users, picked, setPicked }) => {
  const [q, setQ] = useState("");
  const list = users.filter((u) => u.role !== "super_admin" && `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase()));
  const toggle = (id) => setPicked(picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id]);
  return (
    <div className="border border-[#d5dbde]">
      <div className="relative border-b border-[#d5dbde]">
        <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#6b7280]" />
        <input data-testid="bc-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente…" className="w-full bg-transparent pl-8 pr-2 h-9 text-sm text-[#2B3036] outline-none" />
      </div>
      <div className="max-h-44 overflow-y-auto">
        {list.map((u) => (
          <label key={u.user_id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-[#f4f6f7] cursor-pointer">
            <input data-testid={`bc-pick-${u.user_id}`} type="checkbox" checked={picked.includes(u.user_id)} onChange={() => toggle(u.user_id)} className="accent-[#E0262C]" />
            <span className="truncate">{u.name}</span><span className="text-xs font-mono text-[#6b7280] truncate">{u.email}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

const History = ({ items }) => !items.length ? null : (
  <div className="space-y-1.5">
    <div className="text-[10px] uppercase font-mono text-[#6b7280]">Ultimi invii</div>
    {items.slice(0, 5).map((b) => (
      <div key={b.id} data-testid={`bc-history-${b.id}`} className="border border-[#d5dbde] px-3 py-2 text-xs">
        <div className="text-[#3f454c] truncate">{b.text}</div>
        <div className="font-mono text-[#6b7280] mt-0.5">{SEG_LABEL[b.segment]} · {new Date(b.created_at).toLocaleString("it-CH")} · inviati {b.sent}/{b.count} · letti {b.read}</div>
      </div>
    ))}
  </div>
);

const Dialog = ({ users, onClose, onSent }) => {
  const [segment, setSegment] = useState("all");
  const [picked, setPicked] = useState([]);
  const [text, setText] = useState("");
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  useEffect(() => { adminApi.broadcasts().then((d) => setHistory(d.broadcasts)).catch(() => {}); }, []);
  useEffect(() => {
    if (segment === "manual") { setCount(picked.length); return; }
    adminApi.broadcastPreview(segment).then((d) => setCount(d.count)).catch(() => setCount(null));
  }, [segment, picked.length]);
  const send = async () => {
    if (!window.confirm(`Inviare il messaggio a ${count} clienti? Riceveranno chat, notifica ed email.`)) return;
    setBusy(true);
    try {
      const r = await adminApi.broadcast({ text, segment, user_ids: segment === "manual" ? picked : [] });
      toast.success(`Invio avviato a ${r.count} clienti`);
      onSent();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invio non riuscito"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="broadcast-dialog" className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-[#d5dbde] w-full max-w-xl max-h-[90vh] overflow-y-auto p-5 space-y-4 text-[#2B3036]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl flex items-center gap-2"><Megaphone className="w-5 h-5 text-[#E0262C]" /> Messaggio a più clienti</h3>
          <button data-testid="broadcast-close" onClick={onClose} className="text-[#5b6168] hover:text-[#E0262C]"><X className="w-5 h-5" /></button>
        </div>
        <select data-testid="bc-segment" value={segment} onChange={(e) => setSegment(e.target.value)} className={field}>
          {SEGMENTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {segment === "manual" && <ManualPicker users={users} picked={picked} setPicked={setPicked} />}
        <textarea data-testid="bc-text" value={text} onChange={(e) => setText(e.target.value)} rows={5} maxLength={2000}
          placeholder="Es. Ciao {nome}, questo mese hai il 15% di sconto sulla tua prossima campagna con il codice ESTATE15!" className={`${field} resize-none`} />
        <div className="text-[11px] font-mono text-[#6b7280]">{"{nome}"} = nome del cliente · arriva nella chat di ciascuno, con notifica sul telefono ed email</div>
        <button data-testid="bc-send" onClick={send} disabled={busy || !text.trim() || !count}
          className="w-full h-11 aurora-gradient text-white text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-40">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Invia a <span data-testid="bc-count">{count ?? "…"}</span> clienti
        </button>
        <History items={history} />
      </div>
    </div>
  );
};

export const BroadcastButton = ({ users, onSent }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button data-testid="broadcast-open" onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium aurora-gradient text-white hover:opacity-90">
        <Megaphone className="w-3.5 h-3.5" /> Messaggio a tutti / a un gruppo
      </button>
      {open && <Dialog users={users} onClose={() => setOpen(false)} onSent={onSent} />}
    </>
  );
};
